/**
 * CRO PDP helpers: variant-synced save badge + sticky ATC that reuses the real product form.
 * Does not alter app scripts or form fields — only mirrors state and targets form by id.
 */
(function () {
  'use strict';

  function getProductInfo() {
    return document.querySelector('product-info');
  }

  /** Updates the percent-off badge beside the price when variants change. */
  function initSaveBadge() {
    const productInfo = getProductInfo();
    if (!productInfo || typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const sectionId = productInfo.dataset.section;
    const wrap = document.getElementById(`CroSaveBadge-${sectionId}`);
    if (!wrap) return;

    const inner = wrap.querySelector('[data-cro-save-inner]');
    const prefix = wrap.dataset.croSavePrefix || 'Save ';

    function applyVariant(variant) {
      if (!inner) return;
      if (!variant || !variant.compare_at_price || variant.compare_at_price <= variant.price) {
        wrap.setAttribute('hidden', '');
        inner.textContent = '';
        return;
      }
      const pct = Math.round(
        ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
      );
      inner.textContent = `${prefix}${pct}%`;
      wrap.removeAttribute('hidden');
    }

    subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
      applyVariant(evt && evt.data ? evt.data.variant : null);
    });
  }

  /**
   * Selects the Bundler quantity tier on the product form (e.g. Buy 2).
   * @returns {boolean} True when the tier radio was found and activated.
   */
  function selectBundlerQuantityTier(productInfo, quantity) {
    const tierRadio = productInfo.querySelector(
      `input[name="bundle_quantity"][value="${quantity}"]`
    );
    if (!tierRadio) return false;

    tierRadio.checked = true;
    tierRadio.dispatchEvent(new Event('change', { bubbles: true }));
    tierRadio.dispatchEvent(new Event('input', { bubbles: true }));

    const label = tierRadio.closest('label') || productInfo.querySelector(`label[for="${tierRadio.id}"]`);
    label?.click();

    return true;
  }

  /** Forces one-time purchase when Seal Subscriptions radios are present. */
  function selectOneTimePurchase(productInfo) {
    const oneTime = productInfo.querySelector('input[name^="subs_type_"][value="one_time"]');
    if (!oneTime) return;
    oneTime.checked = true;
    oneTime.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Theme preview hosts cannot complete Shop Pay checkout — send shoppers to cart instead. */
  function isThemePreviewHost() {
    return /^(127\.0\.0\.1|localhost)$/i.test(window.location.hostname);
  }

  /** Sends the shopper to checkout using Shopify's native cart POST. */
  function redirectToCheckout() {
    const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
    const checkoutForm = document.createElement('form');
    checkoutForm.method = 'POST';
    checkoutForm.action = cartUrl;
    checkoutForm.style.display = 'none';

    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = '';
    checkoutForm.appendChild(checkoutInput);

    document.body.appendChild(checkoutForm);
    checkoutForm.submit();
  }

  /** Continues to checkout after the offer was added (preview falls back to cart). */
  function goToCheckoutAfterAdd() {
    if (isThemePreviewHost()) {
      window.location.href = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
      return;
    }

    const notificationCheckout = document.querySelector('#cart-notification-form [name="checkout"]');
    if (notificationCheckout) {
      notificationCheckout.click();
      return;
    }

    redirectToCheckout();
  }

  /**
   * Selects Buy N, uses the real Add to cart button, then opens checkout.
   * Matches manual Buy 2 + ATC so Bundler discounts stay applied.
   */
  function submitClaimOffer(productInfo, form, claimQty, claimBtn) {
    selectOneTimePurchase(productInfo);
    const tierSelected = selectBundlerQuantityTier(productInfo, claimQty);

    claimBtn.classList.add('is-loading');
    claimBtn.setAttribute('aria-busy', 'true');

    let checkoutStarted = false;
    let unsubscribeCartUpdate = null;

    /** Runs once after Dawn confirms the cart was updated. */
    function continueToCheckout() {
      if (checkoutStarted) return;
      checkoutStarted = true;
      if (unsubscribeCartUpdate) unsubscribeCartUpdate();
      window.setTimeout(goToCheckoutAfterAdd, 100);
    }

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
        if (evt && evt.source && evt.source !== 'product-form') return;
        continueToCheckout();
      });
    }

    window.setTimeout(function () {
      if (!tierSelected) {
        const qtyInput = form.querySelector('input[name="quantity"]');
        if (qtyInput) qtyInput.value = String(claimQty);
      }

      const mainBtn = productInfo.querySelector('[id^="ProductSubmitButton-"]');
      if (!mainBtn || mainBtn.disabled) {
        if (unsubscribeCartUpdate) unsubscribeCartUpdate();
        claimBtn.classList.remove('is-loading');
        claimBtn.removeAttribute('aria-busy');
        return;
      }

      mainBtn.click();

      /** Fallback if cartUpdate never fires. */
      window.setTimeout(function () {
        if (checkoutStarted) return;

        fetch(((typeof routes !== 'undefined' && routes.cart_url) || '/cart') + '.js', {
          credentials: 'same-origin',
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            if (!cart.item_count) {
              throw new Error('Cart empty after add');
            }
            continueToCheckout();
          })
          .catch(function (err) {
            console.error('[tava sticky claim offer]', err);
            if (unsubscribeCartUpdate) unsubscribeCartUpdate();
            claimBtn.classList.remove('is-loading');
            claimBtn.removeAttribute('aria-busy');
          });
      }, 3000);
    }, 150);
  }

  /**
   * Sticky "Claim Offer" — mirrors Buy 2 + add to cart, then opens checkout.
   * Uses the live product form so variant + Bundler discounts stay in sync.
   */
  function initStickyAtc() {
    const sticky = document.querySelector('[data-tava-sticky-atc]');
    const claimBtn = sticky?.querySelector('[data-tava-sticky-claim-offer]');
    if (!sticky || !claimBtn) return;

    const claimQty = parseInt(sticky.dataset.tavaClaimQuantity || '2', 10) || 2;
    const productInfo = getProductInfo();
    const form = document.querySelector('product-form form[data-type="add-to-cart-form"]');

    /** Disable claim CTA when the main add-to-cart button is unavailable. */
    function syncClaimAvailability() {
      if (!productInfo) return;
      const mainBtn = productInfo.querySelector('[id^="ProductSubmitButton-"]');
      if (mainBtn) claimBtn.disabled = mainBtn.disabled;
    }

    syncClaimAvailability();

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, syncClaimAvailability);
    }

    const mainBtn = productInfo && productInfo.querySelector('[id^="ProductSubmitButton-"]');
    if (mainBtn) {
      const obs = new MutationObserver(syncClaimAvailability);
      obs.observe(mainBtn, { attributes: true, attributeFilter: ['disabled', 'class'] });
    }

    claimBtn.addEventListener('click', function () {
      if (claimBtn.disabled || claimBtn.classList.contains('is-loading')) return;
      if (!form || !productInfo) return;

      submitClaimOffer(productInfo, form, claimQty, claimBtn);
    });

    /** Reveal sticky bar after scrolling past the main buy buttons. */
    const sentinel =
      document.querySelector('.product-form__buttons') ||
      document.querySelector('[id^="ProductSubmitButton-"]');
    if (!sentinel || !('IntersectionObserver' in window)) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { rootMargin: '0px 0px 0px 0px', threshold: 0 }
    );
    io.observe(sentinel);
  }

  /**
   * Main gallery prev/next — Dawn scrolls the slider, but desktop thumbnail mode hides
   * inactive slides so only one slide has width and arrow clicks scroll to NaN.
   * Rebind arrows to media-gallery.setActiveMedia instead.
   */
  function initGalleryNavButtons() {
    const gallery = document.querySelector('product-info media-gallery');
    if (!gallery || gallery.dataset.tavaNavBound === 'true') return;
    gallery.dataset.tavaNavBound = 'true';

    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const thumbList = gallery.querySelector('[id^="Slider-Thumbnails-"]');
    if (!viewer) return;

    let prevBtn = viewer.querySelector('button[name="previous"]');
    let nextBtn = viewer.querySelector('button[name="next"]');
    if (!prevBtn || !nextBtn) return;

    /** Ordered media ids from thumbnails, falling back to main viewer slides. */
    function getMediaIds() {
      const thumbItems = thumbList
        ? Array.from(thumbList.querySelectorAll('.thumbnail-list__item[data-target]'))
        : [];
      if (thumbItems.length) {
        return thumbItems.map((item) => item.dataset.target);
      }
      return Array.from(viewer.querySelectorAll('[data-media-id]')).map((slide) => slide.dataset.mediaId);
    }

    /** Index of the active slide in the ordered media list. */
    function getActiveIndex(mediaIds) {
      if (thumbList) {
        const thumbItems = Array.from(thumbList.querySelectorAll('.thumbnail-list__item[data-target]'));
        const thumbIdx = thumbItems.findIndex((item) => item.querySelector('.thumbnail[aria-current="true"]'));
        if (thumbIdx >= 0) return thumbIdx;
      }
      const activeSlide = viewer.querySelector('[data-media-id].is-active');
      if (!activeSlide) return -1;
      return mediaIds.indexOf(activeSlide.dataset.mediaId);
    }

    /** Disables prev/next at the first and last slide. */
    function updateButtonStates() {
      const mediaIds = getMediaIds();
      const idx = getActiveIndex(mediaIds);
      if (idx < 0 || mediaIds.length < 2) return;

      if (idx <= 0) prevBtn.setAttribute('disabled', '');
      else prevBtn.removeAttribute('disabled');

      if (idx >= mediaIds.length - 1) nextBtn.setAttribute('disabled', '');
      else nextBtn.removeAttribute('disabled');
    }

    /** Switches the main image by one step using the theme media-gallery API. */
    function goToDelta(delta) {
      const mediaIds = getMediaIds();
      if (mediaIds.length < 2 || typeof gallery.setActiveMedia !== 'function') return;

      const idx = getActiveIndex(mediaIds);
      if (idx < 0) return;

      const nextIdx = idx + delta;
      if (nextIdx < 0 || nextIdx >= mediaIds.length) return;

      gallery.setActiveMedia(mediaIds[nextIdx], false);
      updateButtonStates();
    }

    /** Clone buttons so Dawn's broken scroll handlers are removed. */
    function rebindButton(btn, delta) {
      const fresh = btn.cloneNode(true);
      btn.replaceWith(fresh);
      fresh.addEventListener('click', function (evt) {
        evt.preventDefault();
        if (fresh.hasAttribute('disabled')) return;
        goToDelta(delta);
      });
      return fresh;
    }

    prevBtn = rebindButton(prevBtn, -1);
    nextBtn = rebindButton(nextBtn, 1);

    viewer.addEventListener('slideChanged', updateButtonStates);

    if (thumbList) {
      thumbList.querySelectorAll('button.thumbnail').forEach((btn) => {
        btn.addEventListener('click', () => window.setTimeout(updateButtonStates, 0));
      });
    }

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => window.setTimeout(updateButtonStates, 0));
    }

    updateButtonStates();
  }

  /** PDP gallery: show 4 upcoming previews; hide the active main image from the strip. */
  function initGalleryPreviewWindow() {
    const gallery = document.querySelector('product-info media-gallery');
    if (!gallery || gallery.dataset.tavaPreviewBound === 'true') return;
    gallery.dataset.tavaPreviewBound = 'true';

    const thumbList = gallery.querySelector('[id^="Slider-Thumbnails-"]');
    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    if (!thumbList || !viewer) return;

    const items = Array.from(thumbList.querySelectorAll('.thumbnail-list__item[data-target]'));
    if (items.length < 2) return;

    /** Returns index of the thumbnail matching the current main slide. */
    function getActiveIndex() {
      return items.findIndex((item) => item.querySelector('.thumbnail[aria-current="true"]'));
    }

    /** Shows only the next 4 carousel images; active slide stays out of the preview row. */
    function updatePreviewWindow() {
      const total = items.length;
      const activeIdx = getActiveIndex();
      if (activeIdx < 0) return;

      const previewCount = Math.min(4, total - 1);
      const visibleIndices = new Set();
      for (let step = 1; step <= previewCount; step += 1) {
        visibleIndices.add((activeIdx + step) % total);
      }

      items.forEach((item, idx) => {
        item.classList.toggle('tava-preview-hidden', !visibleIndices.has(idx));
      });

      thumbList.scrollTo({ left: 0, behavior: 'smooth' });
    }

    viewer.addEventListener('slideChanged', () => {
      requestAnimationFrame(updatePreviewWindow);
    });

    thumbList.querySelectorAll('button.thumbnail').forEach((btn) => {
      btn.addEventListener('click', () => {
        setTimeout(updatePreviewWindow, 0);
      });
    });

    const observer = new MutationObserver(() => updatePreviewWindow());
    thumbList.querySelectorAll('button.thumbnail').forEach((btn) => {
      observer.observe(btn, { attributes: true, attributeFilter: ['aria-current'] });
    });

    updatePreviewWindow();
  }

  /**
   * Auto-advances the main product gallery every 7s while the shopper is idle.
   * Resets the timer on taps, swipes, or arrow/thumbnail clicks inside the gallery.
   */
  function initGalleryAutoAdvance() {
    const gallery = document.querySelector('product-info media-gallery');
    if (!gallery || gallery.dataset.tavaAutoAdvanceBound === 'true') return;
    gallery.dataset.tavaAutoAdvanceBound = 'true';

    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const thumbList = gallery.querySelector('[id^="Slider-Thumbnails-"]');
    if (!viewer) return;

    const thumbItems = thumbList
      ? Array.from(thumbList.querySelectorAll('.thumbnail-list__item[data-target]'))
      : [];
    const slideCount = thumbItems.length || viewer.querySelectorAll('[data-media-id]').length;
    if (slideCount < 2) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const IDLE_MS = 7000;
    let idleTimer = null;

    /** Moves to the next slide, looping back to the first at the end. */
    function advanceSlide() {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      const nativeScrollTo = window.scrollTo.bind(window);

      /** Keeps the page from jumping when Dawn syncs the active gallery slide. */
      function restorePageScroll() {
        nativeScrollTo({ top: scrollY, left: scrollX, behavior: 'instant' });
      }

      let blockWindowScroll = true;
      window.scrollTo = function () {
        if (blockWindowScroll) return;
        return nativeScrollTo.apply(window, arguments);
      };

      try {
        if (thumbItems.length >= 2 && typeof gallery.setActiveMedia === 'function') {
          const activeIdx = thumbItems.findIndex((item) =>
            item.querySelector('.thumbnail[aria-current="true"]')
          );
          const nextIdx = activeIdx < 0 ? 0 : (activeIdx + 1) % thumbItems.length;
          const mediaId = thumbItems[nextIdx].dataset.target;
          if (mediaId) gallery.setActiveMedia(mediaId, false);
        } else if (typeof viewer.setSlidePosition === 'function' && viewer.slider) {
          const nextBtn = viewer.querySelector('button[name="next"]');
          if (nextBtn && !nextBtn.hasAttribute('disabled')) {
            const step = parseInt(nextBtn.dataset.step || '1', 10);
            viewer.setSlidePosition(viewer.slider.scrollLeft + step * viewer.sliderItemOffset);
          } else {
            viewer.setSlidePosition(0);
          }
        }
      } finally {
        restorePageScroll();
        requestAnimationFrame(restorePageScroll);
        window.setTimeout(function () {
          blockWindowScroll = false;
          window.scrollTo = nativeScrollTo;
          restorePageScroll();
        }, 120);
      }
    }

    function clearIdleTimer() {
      if (idleTimer) {
        window.clearTimeout(idleTimer);
        idleTimer = null;
      }
    }

    /** Starts (or restarts) the 7s idle countdown before the next auto-advance. */
    function scheduleAdvance() {
      clearIdleTimer();
      if (reducedMotion.matches || document.hidden) return;

      idleTimer = window.setTimeout(function () {
        advanceSlide();
        scheduleAdvance();
      }, IDLE_MS);
    }

    function onUserActivity() {
      scheduleAdvance();
    }

    ['click', 'touchstart', 'pointerdown'].forEach(function (evt) {
      gallery.addEventListener(evt, onUserActivity, { passive: true });
    });
    viewer.addEventListener('scroll', onUserActivity, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) clearIdleTimer();
      else scheduleAdvance();
    });

    reducedMotion.addEventListener('change', function () {
      if (reducedMotion.matches) clearIdleTimer();
      else scheduleAdvance();
    });

    scheduleAdvance();
  }

  /** Review carousel: arrow buttons scroll by one card width; track supports touch swipe. */
  function initTestimonialCarousels() {
    document.querySelectorAll('[data-tava-testimonials]').forEach((root) => {
      const track = root.querySelector('[data-tava-testimonials-track]');
      const prev = root.querySelector('[data-tava-testimonials-prev]');
      const next = root.querySelector('[data-tava-testimonials-next]');
      if (!track || !prev || !next) return;

      /**
       * Returns scroll distance for one “step”: first card width + flex gap.
       * @returns {number}
       */
      function pageDelta() {
        const card = track.querySelector('.tava-pdp-testimonials__card');
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
        if (card) {
          const w = card.getBoundingClientRect().width;
          if (w > 0) return w + gap;
        }
        const vp = track.parentElement;
        if (!vp || !vp.clientWidth) return 320;
        return Math.max(280, vp.clientWidth * 0.87);
      }

      function slide(dir) {
        track.scrollBy({ left: dir * pageDelta(), behavior: 'smooth' });
      }

      prev.addEventListener('click', () => slide(-1));
      next.addEventListener('click', () => slide(1));
    });
  }

  /**
   * Ingredient carousel — auto-advance, swipe scroll, prev/next, dot nav.
   * Pauses auto-play after user interaction; respects reduced motion.
   */
  function initIngredientsCarousels() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const PAUSE_AFTER_USER_MS = 12000;

    document.querySelectorAll('[data-tava-ingredients-carousel]').forEach((root) => {
      const track = root.querySelector('[data-tava-ingredients-track]');
      const prevBtn = root.querySelector('[data-tava-ingredients-prev]');
      const nextBtn = root.querySelector('[data-tava-ingredients-next]');
      const dots = Array.from(root.querySelectorAll('[data-tava-ingredients-dot]'));
      if (!track) return;

      const cards = () => Array.from(track.querySelectorAll('.tava-pdp-ingredient'));
      if (!cards().length) return;

      const intervalSec = parseInt(root.getAttribute('data-tava-ingredients-interval'), 10) || 8;
      const intervalMs = Math.max(4000, intervalSec * 1000);
      let timer = null;
      let userPausedUntil = 0;
      let scrollSyncTimer = null;
      let isProgrammaticScroll = false;

      /** Reads active slide index from whichever card is nearest the track start. */
      function activeIndex() {
        const list = cards();
        const anchor = track.scrollLeft;
        let closest = 0;
        let minDist = Infinity;
        list.forEach((card, i) => {
          const dist = Math.abs(card.offsetLeft - anchor);
          if (dist < minDist) {
            minDist = dist;
            closest = i;
          }
        });
        return closest;
      }

      /** Syncs active card + dot states. */
      function setActiveState(index) {
        cards().forEach((card, i) => {
          card.classList.toggle('is-active', i === index);
        });
        dots.forEach((dot, i) => {
          const on = i === index;
          dot.classList.toggle('is-active', on);
          dot.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }

      /** Scrolls track to a card index; loops at ends. */
      function scrollToIndex(index, behavior, fromUser) {
        const list = cards();
        const len = list.length;
        const i = ((index % len) + len) % len;
        const target = list[i];
        if (!target) return;
        if (!fromUser) isProgrammaticScroll = true;
        target.scrollIntoView({
          behavior: behavior || 'smooth',
          inline: 'start',
          block: 'nearest',
        });
        setActiveState(i);
        if (!fromUser) {
          window.setTimeout(() => {
            isProgrammaticScroll = false;
          }, 450);
        }
      }

      function advance() {
        scrollToIndex(activeIndex() + 1);
      }

      function onUserInteract() {
        userPausedUntil = Date.now() + PAUSE_AFTER_USER_MS;
        scheduleAuto();
      }

      function clearAuto() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      function scheduleAuto() {
        clearAuto();
        if (reducedMotion.matches) return;
        timer = setInterval(() => {
          if (document.hidden) return;
          if (Date.now() < userPausedUntil) return;
          advance();
        }, intervalMs);
      }

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) scheduleAuto();
              else clearAuto();
            });
          },
          { threshold: 0.2 }
        );
        io.observe(root);
      } else {
        scheduleAuto();
      }

      prevBtn?.addEventListener('click', () => {
        scrollToIndex(activeIndex() - 1, 'smooth', true);
        onUserInteract();
      });

      nextBtn?.addEventListener('click', () => {
        scrollToIndex(activeIndex() + 1, 'smooth', true);
        onUserInteract();
      });

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.getAttribute('data-tava-ingredients-dot'), 10);
          if (!Number.isNaN(idx)) {
            scrollToIndex(idx, 'smooth', true);
            onUserInteract();
          }
        });
      });

      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollSyncTimer);
          scrollSyncTimer = setTimeout(() => {
            setActiveState(activeIndex());
            if (!isProgrammaticScroll) onUserInteract();
          }, 80);
        },
        { passive: true }
      );

      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollToIndex(activeIndex() + 1, 'smooth', true);
          onUserInteract();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollToIndex(activeIndex() - 1, 'smooth', true);
          onUserInteract();
        }
      });

      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) clearAuto();
        else scheduleAuto();
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) clearAuto();
        else scheduleAuto();
      });

      setActiveState(0);
    });
  }

  /** Soft fade/slide-in for PDP sections (theme-agnostic). */
  function initRevealOnScroll() {
    const nodes = document.querySelectorAll('.tava-pdp-reveal');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach((el) => el.classList.add('is-inview'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-inview');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -24px 0px' }
    );
    nodes.forEach((el) => io.observe(el));
  }

  /** Runs all safe PDP enhancements once DOM is ready. */
  function boot() {
    initSaveBadge();
    initStickyAtc();
    initGalleryNavButtons();
    initGalleryPreviewWindow();
    initGalleryAutoAdvance();
    initTestimonialCarousels();
    initIngredientsCarousels();
    initRevealOnScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
